"use client";

import React from "react";
import { FormModal } from "@/components/ui/form-modal";
import { Button } from "@/components/ui/button";
import type { User } from "@/lib/types/user";
import { GrantAccessEmployeeSection } from "../components/GrantAccessEmployeeSection";
import { ClientsFieldSection } from "./grant-employee-access/ClientsFieldSection";
import { useGrantEmployeeAccessModal } from "./grant-employee-access/useGrantEmployeeAccessModal";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { X } from "lucide-react";

interface GrantEmployeeAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  user?: User;
}

export const GrantEmployeeAccessModal: React.FC<GrantEmployeeAccessModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  user,
}) => {
  const {
    form,
    formManager,
    setContactIds,
    setGrantToAllClients,
    setUserId,
    clientSearchTerm,
    setClientSearchTerm,
    displayedClients,
    clientsLoading,
    isClientSearching,
    grantAccessMutation,
    handleSubmit,
    existingGrants,
    existingLoading,
    revokeAccessMutation,
    handleRevoke,
  } = useGrantEmployeeAccessModal({ isOpen, onClose, onSuccess, userId: user?.id });

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Grant Employee Access to Clients"
      formManager={formManager}
      maxWidth="lg"
      side="bottom"
      forceDrawer={false}
    >
      <form onSubmit={handleSubmit} className="space-y-6 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <GrantAccessEmployeeSection
            userId={form.watch("userId")}
            onUserSelect={setUserId}
            error={form.formState.errors.userId?.message}
            preSelectedUser={user}
          />

          <ClientsFieldSection
            form={form}
            displayedClients={displayedClients}
            clientSearchTerm={clientSearchTerm}
            setClientSearchTerm={setClientSearchTerm}
            isClientSearching={isClientSearching}
            clientsLoading={clientsLoading}
            setContactIds={setContactIds}
            setGrantToAllClients={setGrantToAllClients}
          />
        </div>

        {form.watch("userId") && (
          <div className="rounded border p-3">
            <div className="text-sm font-medium mb-2">Existing client access</div>
            {existingLoading ? (
              <LoadingSpinner />
            ) : existingGrants.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No client access granted yet.
              </p>
            ) : (
              <ul className="space-y-2">
                {existingGrants.map((grant) => (
                  <li
                    key={grant.id}
                    className="flex items-center justify-between gap-2 rounded border px-2 py-1.5"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">
                        {grant.contact?.name || grant.contact_id}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {grant.contact?.company_name || ""}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevoke(grant.contact_id)}
                      disabled={revokeAccessMutation.isPending}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Revoke
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4 border-t border-gray-200 sticky bottom-0 bg-card dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={grantAccessMutation.isPending}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={grantAccessMutation.isPending || clientsLoading}
            className="w-full sm:w-auto"
          >
            {grantAccessMutation.isPending ? "Granting..." : "Grant Access"}
          </Button>
        </div>
      </form>
    </FormModal>
  );
};

// Re-export the in-stack form variant for backwards-compatible imports from
// `@/components/admin/access/GrantEmployeeAccessModal`.
export { GrantEmployeeAccessForm } from "./grant-employee-access/GrantEmployeeAccessForm";
